import { Container, Section, Grid } from '../layout';
import { Card, Icon } from '../ui';
import otherServicesData from '../../data/other-services.json';
import type { OtherService } from '../../types/firm';

export const OtherServicesSection = () => {
  const otherServices: OtherService[] = otherServicesData;

  return (
    <Section id="other-services" padding="xl" className="bg-background-light">
      <Container>
        <div className="text-center mb-16">
          <h2 className="heading-lg text-primary-navy mb-6">
            Other Services
          </h2>
          <p className="body-lg text-text-medium max-w-3xl mx-auto">
            Beyond our core practice areas, we offer specialized legal services to meet diverse client needs. 
            Our comprehensive approach ensures professional support across various legal requirements.
          </p>
        </div>

        <Grid cols={{ default: 1, md: 2, lg: 3 }} gap="lg">
          {otherServices.map((service, index) => (
            <Card 
              key={index} 
              variant="hover" 
              className="p-6 h-full flex flex-col group cursor-pointer"
            >
              {/* Icon and Title */}
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-accent-gold/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-accent-gold/20 transition-colors duration-300">
                  <Icon 
                    name={service.icon as any} 
                    size="lg" 
                    className="text-accent-gold" 
                  />
                </div>
                <div className="flex-1">
                  <h3 className="heading-md text-primary-navy mb-2 group-hover:text-accent-gold transition-colors duration-300">
                    {service.title}
                  </h3>
                </div>
              </div>

              {/* Description */}
              <p className="body-base text-text-medium mb-4 flex-1">
                {service.description}
              </p>

              {/* Service Details */}
              <div className="mt-auto">
                <h4 className="font-semibold text-text-dark mb-3 text-sm uppercase tracking-wide">
                  Service Includes
                </h4>
                <ul className="space-y-2 mb-4">
                  {service.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start">
                      <Icon 
                        name="check" 
                        size="sm" 
                        className="text-accent-gold mr-2 mt-0.5 flex-shrink-0" 
                      />
                      <span className="body-sm text-text-medium">
                        {detail}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Disclaimer if present */}
                {service.disclaimer && (
                  <div className="mt-4 p-3 bg-background-white rounded border-l-4 border-accent-gold/30">
                    <p className="body-sm text-text-medium italic">
                      <strong>Note:</strong> {service.disclaimer}
                    </p>
                  </div>
                )}
              </div>

              {/* Hover Effect Indicator */}
              <div className="mt-4 pt-4 border-t border-border-subtle opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center text-accent-gold">
                  <span className="body-sm font-medium mr-2">Learn More</span>
                  <Icon name="chevronRight" size="sm" />
                </div>
              </div>
            </Card>
          ))}
        </Grid>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-background-white rounded-lg p-8">
            <h3 className="heading-md text-primary-navy mb-4">
              Professional Legal Support
            </h3>
            <p className="body-lg text-text-medium mb-6 max-w-2xl mx-auto">
              Our specialized services are designed to provide comprehensive legal support for your unique requirements. 
              Contact us to discuss how we can assist with your specific legal needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="btn-base btn-primary"
              >
                Get Professional Assistance
              </button>
              <button 
                onClick={() => {
                  const practiceAreasSection = document.getElementById('practice-areas');
                  if (practiceAreasSection) {
                    practiceAreasSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="btn-base btn-secondary"
              >
                View Practice Areas
              </button>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
};